import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Container, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AddLawyerForm from './components/AddLawyerForm';
import EditLawyerForm from './components/EditLawyerForm';
import LawyerList from './components/LawyerList';
import ManagementList from './components/ManagementList';
import LawyerDetail from './components/LawyerDetail';
import ServiceProviderDetail from './components/ServiceProviderDetail';
import AddServiceProviderForm from './components/AddServiceProviderForm';
import EditServiceProviderForm from './components/EditServiceProviderForm';
import LawyerRegistration from './components/LawyerRegistration';
import EmailVerification from './components/EmailVerification';
import EditRegistrationRequest from './components/EditRegistrationRequest';
import EditRegistrationToken from './components/EditRegistrationToken';
import EditRegistrationForm from './components/EditRegistrationForm';
import LawyerDashboard from './components/LawyerDashboard';
import ReferralRequestForm from './components/ReferralRequestForm';
import ServiceProviderRegistrationForm from './components/ServiceProviderRegistrationForm';
import AgencyEmailVerification from './components/AgencyEmailVerification';
import ServiceProviderEditRequest from './components/ServiceProviderEditRequest';
import ServiceProviderEditToken from './components/ServiceProviderEditToken';
import ServiceProviderDashboard from './components/ServiceProviderDashboard';
import ServiceProviderEditForm from './components/ServiceProviderEditForm';
import LoginForm from './components/LoginForm';
// import ProtectedRoute from './components/ProtectedRoute'; // Not needed with global auth

// Public registration route - no authentication required
const PublicRegistration: React.FC = () => {
  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            U.S. Attorney - Registration
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 4 }}>
        <LawyerRegistration />
      </Container>
    </>
  );
};

// Wrapper for public registration-related components
const PublicRegistrationWrapper: React.FC<{ component: React.ComponentType }> = ({ component: Component }) => {
  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            U.S. Lawyer Directory - Registration
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 4 }}>
        <Component />
      </Container>
    </>
  );
};

// Protected application routes - require authentication
const ProtectedApp: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            U.S. Attorneys
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Welcome, {user?.username}
            </Typography>
            <Button color="inherit" component={Link} to="/directory">
              Directory
            </Button>
            <Button color="inherit" component={Link} to="/management">
              Management
            </Button>
            <Button color="inherit" onClick={logout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 4 }}>
        <Routes>
          <Route path="/directory" element={<LawyerList />} />
          <Route path="/directory/lawyer/:id" element={<LawyerDetail />} />
          <Route path="/management" element={<ManagementList />} />
          <Route path="/management/add" element={<AddLawyerForm />} />
          <Route path="/management/edit/:id" element={<EditLawyerForm />} />
          <Route path="/management/lawyer/:id" element={<LawyerDetail />} />

          {/* Agency (legal service provider) management */}
          <Route path="/management/agency/add" element={<AddServiceProviderForm />} />
          <Route path="/management/agency/edit/:id" element={<EditServiceProviderForm />} />
          <Route path="/management/agency/:id" element={<ServiceProviderDetail />} />

          {/* Default redirect to directory */}
          <Route path="/" element={<Navigate to="/directory" replace />} />
        </Routes>
      </Container>
    </>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public registration route - no authentication required */}
      <Route path="/register" element={<PublicRegistration />} />
      
      {/* Public email verification route */}
      <Route path="/verify-email/:token" element={<EmailVerification />} />
      
      {/* Public edit registration routes */}
      <Route path="/register/edit-request" element={<PublicRegistrationWrapper component={EditRegistrationRequest} />} />
      <Route path="/register/edit" element={<PublicRegistrationWrapper component={EditRegistrationToken} />} />
      <Route path="/register/edit-form" element={<PublicRegistrationWrapper component={EditRegistrationForm} />} />
      <Route path="/register/dashboard" element={<PublicRegistrationWrapper component={LawyerDashboard} />} />
      <Route path="/register/referral-request" element={<PublicRegistrationWrapper component={ReferralRequestForm} />} />

      {/* Public agency (legal service provider) registration + self-service routes */}
      <Route path="/register/agency" element={<PublicRegistrationWrapper component={ServiceProviderRegistrationForm} />} />
      <Route path="/register/agency/verify-email/:token" element={<AgencyEmailVerification />} />
      <Route path="/register/agency/edit-request" element={<PublicRegistrationWrapper component={ServiceProviderEditRequest} />} />
      <Route path="/register/agency/edit" element={<PublicRegistrationWrapper component={ServiceProviderEditToken} />} />
      <Route path="/register/agency/dashboard" element={<PublicRegistrationWrapper component={ServiceProviderDashboard} />} />
      <Route path="/register/agency/edit-form" element={<PublicRegistrationWrapper component={ServiceProviderEditForm} />} />
      
      {/* If not authenticated, show login page for protected routes */}
      {!isAuthenticated ? (
        <>
          <Route path="*" element={<LoginForm />} />
        </>
      ) : (
        /* If authenticated, show protected application */
        <Route path="*" element={<ProtectedApp />} />
      )}
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
